import { AxiosInstance, AxiosResponse } from "axios"
import {
  GetDashboardAxiosResponse,
  SaveDashboardAxiosResponse,
  DeleteDashboardAxiosResponse,
} from "types/ReportingDashboard/reporting.types"

/**
 * Dashboard API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Fetches all Dashboards by project ID.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function AvailableDashboards(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get<string>(
    `/available-dashboardfiles?id=${id}&hint_owner=${owner}`
  )
  return response
}

/**
 * Fetches a Dashboard by ID.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The dashboards ID.
 * @param owner - The owner of the project.
 * @param name - The dashboard name.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function getDashboard(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  name: string
): Promise<AxiosResponse<GetDashboardAxiosResponse>> {
  const response = await axiosInstance.get<GetDashboardAxiosResponse>(
    `/dashboardfile-get?id=${id}&hint_owner=${owner}&name=${name}`
  )
  return response
}

/**
 * Saves a dashboard.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The dashboard ID.
 * @param owner - The owner of the project.
 * @param dashboardConfig - The dashboard config.
 * @param name - The dashboard name.
 * @param overwrite - Whether to overwrite existing dashboard.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function saveDashboard(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  dashboardConfig: string,
  name: string,
  overwrite: boolean = false
): Promise<AxiosResponse<SaveDashboardAxiosResponse>> {
  const response = await axiosInstance.post<SaveDashboardAxiosResponse>(
    `/dashboardfile-save?id=${id}&hint_owner=${owner}&name=${name}&overwrite=${overwrite}`,
    dashboardConfig
  )
  return response
}

/**
 * Delete a dashboard.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The dashboard ID.
 * @param owner - The owner of the project.
 * @param name - The dashboard name.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function deleteDashboard(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  name: string
): Promise<AxiosResponse<DeleteDashboardAxiosResponse>> {
  const response = await axiosInstance.delete<DeleteDashboardAxiosResponse>(
    `/dashboardfile-delete?id=${id}&hint_owner=${owner}&name=${name}`
  )
  return response
}
